import React, { useRef, useState } from "react";
import { ExternalDropZone, Upload, type UploadFileInfo } from "@progress/kendo-react-upload";
import { Button } from "@progress/kendo-react-buttons";
import { ProgressBar } from "@progress/kendo-react-progressbars";
import type { PathTypeSchema } from "@domain/schemas/BI";
import { useProjectPermission } from "@presentation/components/helpers/usePermissionCheck";
import { getUploadErrorMessage } from "@presentation/common/ErrorHelpers";
import { useUploadDestinationFlow } from "./useUploadDestinationFlow";
import { UploadDestinationField } from "./UploadDestinationField";
import type { UploadDestination } from "./UploadDestinationPickerDialog";
import { ProjectActions } from "@domain/schemas/workspace";

interface SingleFileUploadProps {
  projectId: string | null;
  pathType: PathTypeSchema;
  allowedExtensions?: string[];
  maxFileSize?: number;
  prerequisites?: string;
  initialDestination?: Partial<UploadDestination>;
  children?: React.ReactNode;
  checkFileExists: (name: string, rootPath: string | undefined, folder: string | undefined) => Promise<number | null>;
  upload: (file: File, destination: UploadDestination, onProgress: (progress: number) => void) => Promise<void>;
}

export const SingleFileUpload: React.FC<SingleFileUploadProps> = ({ projectId, pathType, allowedExtensions, maxFileSize, prerequisites, initialDestination, children, checkFileExists, upload }) => {
  const [files, setFiles] = useState<UploadFileInfo[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [completed, setCompleted] = useState(false);
  const [progress, setProgress] = useState(0);
  const uploadRef = useRef<Upload>(null);
  const { allowed } = useProjectPermission(projectId, ProjectActions.TriggerJob);
  const { dialogs, prepareUpload, pickDestination, destination, error: destinationError, isChoosing } = useUploadDestinationFlow(pathType, projectId, checkFileExists, initialDestination);
  const invalidFiles = files.some((file) => (file.validationErrors?.length ?? 0) > 0);
  const disabled = !allowed || busy || isChoosing;

  const startUpload = async () => {
    if (disabled || prerequisites || invalidFiles || !destination || files.length !== 1) return;
    setError("");
    setCompleted(false);
    setProgress(0);
    setBusy(true);
    try {
      const file = files[0].getRawFile?.();
      if (!file) throw new Error("The selected file is no longer available. Please select it again.");
      const prepared = await prepareUpload(file.name, destination);
      if (!prepared.proceed) return;
      await upload(file, destination, setProgress);
      setProgress(100);
      setCompleted(true);
      setFiles([]);
    } catch (error) {
      setError(getUploadErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minWidth: 360, maxWidth: 600, padding: 12 }}>
      {children}
      {!allowed && <p role="alert">You do not have permission to upload files in this project.</p>}
      {prerequisites && <p role="alert">{prerequisites}</p>}
      <ExternalDropZone uploadRef={uploadRef} />
      <Upload
        ref={uploadRef}
        batch={false}
        multiple={false}
        autoUpload={false}
        files={files}
        disabled={disabled || Boolean(prerequisites)}
        accept={allowedExtensions?.join(",")}
        restrictions={{ allowedExtensions, maxFileSize }}
        showActionButtons={false}
        onAdd={(event) => {
          setFiles(event.affectedFiles);
          setCompleted(false);
          setError("");
        }}
        onRemove={() => {
          setFiles([]);
          setError("");
        }}
      />
      <UploadDestinationField
        destination={destination}
        onChoose={() => void pickDestination(files[0]?.name ?? "Selected file")}
        disabled={disabled || files.length === 0 || invalidFiles || Boolean(prerequisites)}
        error={destinationError}
      />
      {busy && (
        <div role="status">
          Uploading... <ProgressBar value={progress} />
        </div>
      )}
      {completed && <p role="status">Upload completed successfully.</p>}
      {error && (
        <p role="alert" style={{ color: "#b91c1c", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {error}
        </p>
      )}
      <Button type="button" themeColor="primary" onClick={() => void startUpload()} disabled={disabled || !destination || files.length === 0 || invalidFiles || Boolean(prerequisites)}>
        {busy ? "Uploading..." : "Start Upload"}
      </Button>
      {dialogs}
    </div>
  );
};
